/*
 * Industry landing page content — `nl` locale (Belgium market).
 *
 * Belgian Dutch (Vlaams/Nederlands) translation of the Belgium/base variant of
 * every industry. Uses the BELGIUM copy for all fields (the international wording
 * belongs to the `en` set). All Belgium-specific pains/features are retained
 * because the Belgian market sees them.
 *
 * WARNING: the legally-sensitive `compliance` copy is PENDING NATIVE LEGAL
 * REVIEW (G6 / Story 4.10). Dates, rates, thresholds and penalties are carried
 * over faithfully from the source but must be verified by a native legal reviewer.
 *
 * Pure data / zero-import (NFR10) so the build config can import the slug list.
 */

import type { Industry } from "./types";

export const INDUSTRIES_NL: Industry[] = [
  {
    slug: "construction",
    name: "Bouw",
    headline:
      "Tijdsregistratie voor bouwteams — locatiegeverifieerde check-in, projecturen en checkin@work, in één app",
    intro:
      "Ploegen verspreid over werven, uren op papier gekrabbeld, naleving door onderaannemers een constante zorg. Done-it geeft elke werf een waterdichte digitale tijdsregistratie die de inspecteur tevredenstelt en in één klik je loonverwerking voedt.",
    pains: [
      {
        title: "Geen betrouwbaar overzicht van wie waar werkte",
        body: "Met meerdere actieve werven en wisselende ploegen kloppen handmatige urenstaten per definitie niet. Werknemers ronden af, vergeten registraties of vullen ze dagen later in — waardoor je met loongegevens zit die je niet kan verantwoorden.",
      },
      {
        title: "Onderaannemers zijn ook jouw aansprakelijkheid",
        body: "De Belgische wet kent een hoofdelijke aansprakelijkheid: als een onderaannemer zijn werknemers niet registreert (Dimona) of CheckIn@Work niet naleeft, draag jij als hoofdaannemer het inspectierisico. Zonder één overzicht tast je in het duister.",
      },
      {
        title: "Budgetoverschrijdingen komen te laat aan het licht",
        body: "Tegen de tijd dat je merkt dat een werf zijn geplande uren heeft opgebruikt, is de marge al weg. Er is geen live aftelling — alleen een nare verrassing op het moment van facturatie.",
      },
      {
        title: "Loonvoorbereiding kost elke maand uren",
        body: "Urenstaten verzamelen, ze afstemmen op projecten en ze vertalen voor het sociaal secretariaat neemt dagen in beslag. Elke registratie is een mogelijke fout.",
      },
    ],
    features: [
      {
        title: "Locatiegeverifieerde check-in per werf",
        body: "Eén tik checkt de werknemer in, locatiegeverifieerd, en koppelt de uren aan het juiste project. Geen kaartlezers, geen terminals — gewoon de smartphone die je ploeg al op zak heeft. Voedt check-in at work voor Belgische werven.",
      },
      {
        title: "Live resterende uren per project",
        body: "Stel de geplande uren in als budget wanneer je het project opent. Werknemers zien een aftelling van wat er nog rest; jij ziet het live in je dashboard. Budgetoverschrijdingen zijn zichtbaar voordat ze gebeuren.",
      },
      {
        title: "Beheer en aanwezigheid van onderaannemers",
        body: "Voeg onderaannemers toe aan een project, wijs ze toe aan de check-in en volg hun uren samen met je eigen ploeg — in hetzelfde overzicht.",
      },
      {
        title: "CheckIn@Work-synchronisatie",
        body: "Done-it stuurt aanwezigheidsgegevens automatisch door naar het officiële Belgische CheckIn@Work-platform. Geen dubbele invoer, geen handmatige sessies op het RSZ-portaal.",
      },
      {
        title: "Loonexport in één klik",
        body: "Alle geregistreerde uren — per werknemer, per project — exporteer je naar een nette spreadsheet en stuur je rechtstreeks door naar je sociaal secretariaat.",
      },
    ],
    compliance: {
      label: "Check-in at work + wet van 2027",
      title: "De bouw registreert al aanwezigheid — en 2027 voegt er meer aan toe",
      body: "Op Belgische bouwwerven moet de aankomst van elke werknemer geregistreerd worden via check-in at work en doorgestuurd worden naar de RSZ (voor werven boven de drempel). Vandaag is dat enkel de check-in — de aankomst. Vanaf 1 januari 2027 voegt een algemene verplichting een volledige arbeidstijdregistratie toe voor elke werkgever, inclusief de bouw: je moet een betrouwbaar overzicht bijhouden van de volledige arbeidstijd (het begin en het einde van de dag moeten niet automatisch naar de RSZ doorgestuurd worden). Done-it dekt beide — het voedt check-in at work én houdt de waterdichte tijdsregistratie bij die de regels van 2027 vereisen.",
      slug: "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
    },
    workflow: [
      {
        step: "Ploeg komt aan op de werf",
        body: "Elke werknemer tikt één keer om in te checken. GPS bevestigt de locatie, registreert het tijdstip en koppelt de uren aan het project. Geen papier, geen terminal.",
      },
      {
        step: "Je ziet alles live",
        body: "Uren, resterend budget, aanwezigheid van onderaannemers en elk gemeld meerwerk vanaf de werf — allemaal in je dashboard, in real time.",
      },
      {
        step: "Maandeinde in enkele minuten",
        body: "Exporteer alle uren per werknemer en per project. Stuur ze naar je sociaal secretariaat. De gegevens van Done-it voldoen al aan CheckIn@Work en de vereisten van 2027.",
      },
    ],
    stats: [
      { value: "1 tik", label: "om in te checken op elke werf — geen terminal, geen papier" },
      { value: "1 klik", label: "loonexport klaar voor je sociaal secretariaat" },
      { value: "1 jan 2027", label: "algemene verplichting tot tijdsregistratie — wees klaar" },
    ],
    quote: {
      text: "Onze ploeg checkt zichzelf in op de werf. Op het einde van de maand klopt de loonverwerking gewoon — we zijn helemaal gestopt met achter urenstaten aan te lopen.",
      who: "Zaakvoerder, bouwbedrijf · 14 werknemers",
      result: "5 uur per week bespaard op administratie",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "flashcontroles-in-de-belgische-bouwsector-zomer-2025-hoe-vermijd-je-boetes-met-digitale-oplossingen",
    ],
  },

  {
    slug: "cleaning",
    name: "Schoonmaak",
    headline:
      "Tijdsregistratie voor schoonmaakbedrijven — check-in and out at work, aanwezigheidsbewijs en loonverwerking in één app",
    intro:
      "Check-in and out at work (CIAO@work) is verplicht sinds 1 september 2024: realtime in- en uitchecken voor elke schoonmaker, op elke locatie, geen terugwerkende invoer toegestaan. Done-it verzorgt de registratie automatisch en zet ze om in een nette loonexport.",
    pains: [
      {
        title: "Check-in and out at work is verplicht — en omslachtig",
        body: "Elke werknemer moet zelf in real time in- en uitchecken, inclusief pauzes — en dat wordt telkens naar de RSZ doorgestuurd, soms meerdere keren per dag. De werkgever kan het niet in hun plaats doen, en voorafgaande registratie is verboden. Eén gemiste registratie is een inbreuk.",
      },
      {
        title: "Onderaannemers zijn ook jouw aansprakelijkheid",
        body: "De Belgische wet kent een hoofdelijke aansprakelijkheid: als een onderaannemer zijn werknemers niet registreert (Dimona) of check-in and out at work niet naleeft, draag jij als hoofdaannemer het inspectierisico. Zonder één overzicht tast je in het duister.",
      },
      {
        title: "Aanwezigheid bewijzen op elke locatie",
        body: "Klanten en inspecteurs verwachten waterdichte aanwezigheidsregistraties. Manuele lijsten kunnen betwist worden; locatiegeverifieerde digitale check-ins niet.",
      },
      {
        title: "Teams verspreid over veel locaties",
        body: "Met schoonmakers die zich doorheen de dag tussen locaties verplaatsen, heb je geen live zicht op wie waar werkt — tot er iemand belt.",
      },
      {
        title: "Loonfouten door afgeronde uren",
        body: "Zonder nauwkeurige in-/uittijden wordt de loonverwerking uit het geheugen gereconstrueerd. Uren worden naar boven afgerond, kosten lopen op en discussies volgen.",
      },
    ],
    features: [
      {
        title: "Realtime check-in and out",
        body: "Werknemers tikken in en uit op hun telefoon op elke locatie, locatiegeverifieerd; de tijd is exact, met registratie van pauzes. (De officiële check-in and out at work / CIAO@work-integratie maakt deel uit van de Project plans.)",
        plan: "Time Basic",
      },
      {
        title: "Automatische pauze-aftrek",
        body: "Verplichte pauzes worden automatisch van de werkdag afgetrokken — geen handmatig rekenwerk, geen discussies.",
        plan: "Time Basic",
      },
      {
        title: "Live overzicht over meerdere locaties",
        body: "Zie welke schoonmakers op welke locaties zijn op dit moment. Spot lacunes voordat klanten ze opmerken.",
      },
      {
        title: "Afwezigheidsregistratie met digitale ziektebriefjes",
        body: "Werknemers laden ziektebriefjes en afwezigheidsdocumenten rechtstreeks op vanuit de app. Ondersteunt weerverlet en andere afwezigheidstypes die relevant zijn voor de sector.",
      },
      {
        title: "Loonexport in één klik",
        body: "Conforme tijdstempels stromen rechtstreeks door naar een loonklare export voor je sociaal secretariaat. Geen overtypen, geen fouten.",
      },
    ],
    compliance: {
      label: "Check-in and out at work — verplicht sinds sept 2024",
      title: "Check-in and out at work is actief — elk schoonmaakbedrijf moet voldoen",
      body: "Check-in and out at work (CIAO@work) is verplicht voor het professioneel reinigen van onroerende goederen van derden sinds 1 september 2024. Elke werknemer — werknemer, zelfstandige, uitzendkracht of stagiair — moet het begin en het einde van het werk in real time registreren, inclusief pauzes, telkens doorgestuurd naar de RSZ (soms meerdere keren per dag). De verplichting treedt in werking vanaf €30.000 (geen onderaannemer), €5.000 (één onderaannemer), of elk bedrag bij twee of meer onderaannemers. Boetes onder het Sociaal Strafwetboek lopen van €300 tot €6.000 per werknemer. Done-it synchroniseert de in-/uitgegevens automatisch met het officiële platform — geen dubbele invoer.",
      slug: "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
    },
    workflow: [
      {
        step: "Schoonmaker komt aan op de locatie",
        body: "Eén tik checkt hem in, locatiegeverifieerd. Begin en einde van de pauze worden geregistreerd in de app — precies zoals check-in and out at work vereist.",
      },
      {
        step: "Gegevens stromen automatisch naar de RSZ",
        body: "Done-it stuurt de in-/uitgegevens in real time door naar het officiële platform. Je hoeft je nooit handmatig op het portaal aan te melden.",
      },
      {
        step: "Loonverwerking is één klik",
        body: "Alle aanwezigheidsgegevens — met exacte tijden, locaties en pauzes — exporteer je netjes voor je sociaal secretariaat.",
      },
    ],
    stats: [
      { value: "1 sep", label: "2024 — check-in and out at work werd verplicht" },
      { value: "€300–€6k", label: "boete per werknemer bij niet-naleving" },
      { value: "0", label: "dubbele invoeren — Done-it synchroniseert automatisch" },
    ],
    quote: {
      text: "We kunnen eindelijk bewijzen wie waar en wanneer schoonmaakte. Geen aanwezigheidslijsten meer, geen discussies met klanten meer — GPS regelt het.",
      who: "Manager, schoonmaakbedrijf · 9 werknemers",
      result: "Geen enkele afronding van uren naar boven",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    ],
  },

  {
    slug: "installation",
    name: "Installatie",
    headline:
      "Tijdsregistratie voor installatieteams — projecturen, navigatie en budgetbeheer in één app",
    intro:
      "Stuur je monteurs naar het juiste adres met alles wat ze nodig hebben, koppel elk gewerkt uur automatisch aan de juiste job, en zie het resterende budget voordat de werf sluit. Done-it is de eenvoudigste manier om installatieprojecten op tijd en op kosten te houden.",
    pains: [
      {
        title: "Jobinfo bereikt het terrein niet op tijd",
        body: "Ploegen komen aan op een werf zonder het adres, de contactpersoon of de details van de opdracht. Dat eerste telefoontje is tien minuten factureerbare tijd verloren voordat er één uur gewerkt is.",
      },
      {
        title: "Uren zijn niet gekoppeld aan het juiste project",
        body: "Wanneer werknemers hun uren handmatig invullen op het einde van de dag — als ze ze al invullen — belanden uren in de verkeerde job, of verdwijnen ze helemaal. Facturen kloppen niet met de werkelijkheid.",
      },
      {
        title: "Budgetoverschrijdingen worden te laat opgemerkt",
        body: "Zonder een live aftelling van de resterende uren per project kom je er pas achter dat een job is uitgelopen wanneer je de factuur opmaakt — niet wanneer je nog kan reageren.",
      },
      {
        title: "Uren van onderaannemers moeilijk te volgen",
        body: "Onderaannemers die op dezelfde werf werken, gebruiken andere systemen, vullen hun uren apart in of helemaal niet. Op het einde van de maand afstemmen is een traag en foutgevoelig proces.",
      },
    ],
    features: [
      {
        title: "Projectinfo rechtstreeks gedeeld in de app",
        body: "Werknemers openen het project op hun telefoon: adres, geplande uren, contactpersoon en opgeladen bestanden (plannen, instructies, PDF's). Alles wat ze nodig hebben staat er voordat ze aankomen.",
        plan: "Project Basic",
      },
      {
        title: "Navigatie naar de werf met één knop",
        body: "Tik één keer en de telefoon navigeert naar het projectadres — ingebouwd in de app, geen aparte kaartapp nodig.",
      },
      {
        title: "Aftelling van resterende uren per project",
        body: "Stel de geplande uren in wanneer je het project opent. Werknemers zien hoeveel uren er nog rest; jij ziet de live status in je dashboard. Overschrijdingen zijn zichtbaar voordat ze gebeuren.",
        plan: "Project Basic",
      },
      {
        title: "Check-in van werknemers per project",
        body: "Elke werknemer checkt in bij het juiste project. Je weet altijd wie op dit moment op welke werf zit.",
        plan: "Project Basic",
      },
      {
        title: "Registratie van meerwerk & materiaal",
        body: "Wanneer de opdracht op de werf uitbreidt, registreren werknemers meerwerk en materiaal in real time — met tekst en foto's — zodat niets uit het geheugen gefactureerd wordt.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Wet van 2027 + hoofdelijke aansprakelijkheid",
      title: "Installatieteams vallen onder zowel de wet van 2027 als de regels voor onderaannemers",
      body: "Vanaf 1 januari 2027 moet elke werkgever de arbeidsuren registreren met een digitaal, waterdicht systeem. Voor installatiewerk op grote bouwwerven (≥€500.000 excl. btw) is CheckIn@Work-registratie nu al verplicht — en met onderaannemers draag je een hoofdelijke aansprakelijkheid voor hun naleving. Done-it dekt beide: het levert het algemene systeem voor tijdsregistratie dat 2027 vereist en synchroniseert met CheckIn@Work waar de drempel van toepassing is.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Werknemer opent het project op zijn telefoon",
        body: "Adres, contact, opdracht en opgeladen bestanden staan er meteen. Ze navigeren met één tik — geen telefoontjes naar kantoor voor de weg.",
      },
      {
        step: "Uren worden automatisch op de juiste job geregistreerd",
        body: "De check-in koppelt elke gewerkte minuut aan het juiste project. Het resterende budget wordt live bijgewerkt.",
      },
      {
        step: "Meerwerk wordt op de werf vastgelegd",
        body: "Wijzigingen in de opdracht, extra materiaal en vertragingen worden geregistreerd met tekst en foto's op het moment dat ze gebeuren — klaar voor de factuur.",
      },
    ],
    stats: [
      { value: "Real-time", label: "zicht op resterende uren per project" },
      { value: "0", label: "telefoontjes naar kantoor voor werfadressen" },
      { value: "1 jan 2027", label: "algemene verplichting tot tijdsregistratie" },
    ],
    quote: {
      text: "Uren belanden automatisch op het juiste project. Job costing is plots eenvoudig — we zien de echte marge voordat we een job afsluiten.",
      who: "Zaakvoerder, installatiebedrijf · 6 werknemers",
      result: "Werkelijke kost per job, live",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
    ],
  },

  {
    slug: "field-service",
    name: "Buitendienst",
    headline:
      "Tijdsregistratie voor buitendienstteams — kilometers, onkosten en uren onderweg, allemaal in één app",
    intro:
      "Technici die meerdere klanten per dag bezoeken, bonnetjes in dashboardkastjes, kilometers uit het geheugen genoteerd. Done-it legt uren, kilometers en onkosten vast op het terrein — gekoppeld aan de juiste job op het moment dat ze gebeuren.",
    pains: [
      {
        title: "Kilometers dagen later genoteerd — als het al gebeurt",
        body: "Technici vragen hun routes op het einde van de week te reconstrueren, leidt tot onnauwkeurige vergoedingen, discussies met klanten en een groeiende stapel niet-ingediende kilometers.",
      },
      {
        title: "Onkosten op papieren bonnetjes die verdwijnen",
        body: "Een bonnetje in een dashboardkastje is een kost die misschien nooit op een factuur belandt. De werkelijke kost per job is altijd een onderschatting.",
      },
      {
        title: "Geen realtime zicht op waar het team is",
        body: "Zonder live check-ins kom je pas te weten waar een technicus is wanneer er iets misgaat en er iemand belt.",
      },
      {
        title: "Moeilijk om de werkelijke marge per job te zien",
        body: "Wanneer arbeid, verplaatsing en onkosten in aparte systemen worden bijgehouden — of helemaal niet — is er nooit één kostencijfer per job.",
      },
    ],
    features: [
      {
        title: "Automatische kilometerregistratie (Trax-it)",
        body: "GPS registreert vertrek, aankomst en de exacte afstand per rit. Configureer transporttypes (eigen wagen, bedrijfsvoertuig, carpool) met hun eigen vergoedingstarieven. Geen handmatig rittenboek.",
        plan: "Add-on",
      },
      {
        title: "Onkostenbeheer op het terrein",
        body: "Werknemers maken een foto van een bonnetje op het terrein; het wordt aan de job gekoppeld en is klaar voor terugbetaling — niet achternagelopen op het einde van de maand.",
        plan: "Time Basic",
      },
      {
        title: "Projectgekoppeld kostenbeheer",
        body: "Uren, kilometers en onkosten worden in real time opgeteld bij de job. De kost per job is zichtbaar terwijl de job nog open is.",
        plan: "Project Basic",
      },
      {
        title: "Locatiegeverifieerde check-in en live teamoverzicht",
        body: "Elke technicus checkt in op de klantlocatie. Je ziet wie waar is, op dit moment — geen telefoontjes, geen giswerk.",
      },
      {
        title: "Loonexport in één klik",
        body: "Uren en kilometervergoedingen exporteer je netjes voor het sociaal secretariaat — met de officiële Belgische kilometertarieven automatisch toegepast.",
      },
    ],
    compliance: {
      label: "Wet van 2027 + kilometerregels",
      title: "Buitendienst valt onder de algemene verplichting van 2027 — en kilometers hebben hun eigen regels",
      body: "Vanaf 1 januari 2027 moet elke werkgever de uren digitaal registreren. Voor buitendienstwerknemers die tussen klantlocaties reizen, betekent het EU-Tyco-arrest (2015) ook dat de verplaatsing van thuis naar de eerste klant en van de laatste klant naar huis als arbeidstijd telt. De Belgische kilometervergoeding kent officiële maximumtarieven (€0,4449/km jaarlijks, Q2 2025: €0,4320/km) — deze overschrijden vereist een bewijs van de werkelijke kosten. Done-it past het huidige geïndexeerde tarief automatisch toe en signaleert de drempel van 24.000 km/jaar per werknemer.",
      slug: "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    },
    workflow: [
      {
        step: "Technicus vertrekt voor de eerste oproep",
        body: "De check-in start het rittenlogboek. Kilometers worden automatisch vastgelegd van vertrek tot aankomst — geen handmatige invoer.",
      },
      {
        step: "Op de werf",
        body: "Uren koppelen aan het juiste project. Meerwerk, materiaal en bonnetjes worden in de app geregistreerd op het moment dat ze gebeuren.",
      },
      {
        step: "Maandeinde zonder de chaos",
        body: "Elk uur, elke kilometer en elke onkost is al gekoppeld aan de juiste job. Exporteer naar de loonverwerking en factureer met werkelijke kostencijfers.",
      },
    ],
    stats: [
      { value: "€0,4449", label: "per km — officieel Belgisch tarief (jaarlijks, 2025–26)" },
      { value: "24 000", label: "km/jaar-drempel — daarboven moeten werkelijke kosten bewezen worden" },
      { value: "1 jan 2027", label: "algemene verplichting tot tijdsregistratie" },
    ],
    quote: {
      text: "Kilometers waren vroeger een maandelijkse kopzorg. Nu registreren ze zichzelf — elke kilometer is gekoppeld aan de juiste klant nog voor we terug op kantoor zijn.",
      who: "Operationeel manager, buitendienstbedrijf · 11 werknemers",
      result: "Nauwkeurige kost per job, elke keer",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Tijdsregistratie voor HVAC-technici — job costing, onderdelen en verplaatsing in één app",
    intro:
      "Elke serviceoproep heeft drie kosten: arbeid, onderdelen en verplaatsing. Done-it legt alle drie vast op het terrein en telt ze op bij de juiste job — zodat je je marge kent voordat je de factuur afsluit.",
    pains: [
      {
        title: "Serviceoproepen zijn moeilijk nauwkeurig te calculeren",
        body: "Arbeid, onderdelen en verplaatsing leven in verschillende systemen — of verschillende zakken. De werkelijke kost van een interventie wordt pas lang nadat de technicus de werf heeft verlaten duidelijk.",
      },
      {
        title: "Geen bewijs van servicetijd",
        body: "Wanneer een klant betwist hoe lang een herstelling duurde, is een papieren urenstaat makkelijk aan te vechten. Door GPS bevestigde check-in- en check-out-tijdstempels niet.",
      },
      {
        title: "Onderhoudscontracten knagen stilletjes aan de marge",
        body: "Bij terugkerende onderhoudscontracten stapelen kleine overschrijdingen zich op over vele bezoeken. Zonder een live urenteller kom je pas bij verlenging te weten dat het contract verlieslatend is.",
      },
      {
        title: "Technici vergeten pauzes te registreren",
        body: "Handmatige tijdsregistraties missen pauzes, wat leidt tot overbetaling en een loonverwerking die niet overeenstemt met de werkelijkheid.",
      },
    ],
    features: [
      {
        title: "Projectgebaseerde tijdsregistratie",
        body: "Elke serviceoproep of elk onderhoudscontract is een project. Uren, verplaatsing en materiaal worden in real time opgeteld — wat je live zicht op je marge geeft.",
        plan: "Project Basic",
      },
      {
        title: "Meerwerk en materiaal, geregistreerd op het terrein",
        body: "Wanneer een job uitbreidt, registreren technici meerwerk en onderdelen met tekst en foto's voordat ze vertrekken. Niets wordt uit het geheugen gefactureerd.",
        plan: "Project Basic",
      },
      {
        title: "Kilometers en transportkosten",
        body: "Kilometers per interventie worden automatisch geregistreerd en bij de job gecalculeerd. Configureer vergoedingstarieven per voertuigtype.",
        plan: "Add-on",
      },
      {
        title: "Automatische pauze-aftrek",
        body: "Verplichte pauzes worden automatisch van de totale tijd afgetrokken — geen discussies, geen loonfouten.",
      },
      {
        title: "Locatiegeverifieerde check-in en aanwezigheidsbewijs",
        body: "Tijdstempel, door GPS bevestigde check-in op elke werf. Onbetwistbaar bewijs van wanneer de technicus aankwam en vertrok.",
      },
    ],
    compliance: {
      label: "Wet van 2027",
      title: "HVAC-teams vallen onder de algemene verplichting tot tijdsregistratie van 2027",
      body: "Vanaf 1 januari 2027 moet elke Belgische werkgever een digitaal, waterdicht overzicht van de arbeidsuren bijhouden. Voor HVAC-teams die werken op grote bouwwerven (≥€500.000 excl. btw) is CheckIn@Work-registratie nu al vereist. Done-it dekt beide scenario's en exporteert de gegevens rechtstreeks naar je sociaal secretariaat.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Technicus checkt in op de werf",
        body: "GPS zet een tijdstempel op de aankomst en koppelt de uren aan de serviceoproep of het onderhoudscontract. Geen papier, geen terminal.",
      },
      {
        step: "Meerwerk wordt ter plaatse vastgelegd",
        body: "Gebruikte onderdelen, extra uren en wijzigingen in de opdracht worden in de app geregistreerd met foto's — voordat ze de werf verlaten.",
      },
      {
        step: "Jobkost is meteen zichtbaar",
        body: "Arbeid, verplaatsing en materiaal worden in real time bij het project opgeteld. Je ziet de marge voordat je de factuur opmaakt.",
      },
    ],
    stats: [
      { value: "3", label: "kostentypes vastgelegd per oproep: arbeid, verplaatsing, onderdelen" },
      { value: "Live", label: "zicht op de marge per servicecontract" },
      { value: "1 jan 2027", label: "verplichte digitale tijdsregistratie" },
    ],
    quote: {
      text: "We weten eindelijk wat elk servicecontract ons werkelijk kost. De margeverrassingen stopten de dag dat we Done-it begonnen te gebruiken.",
      who: "Zaakvoerder, HVAC-bedrijf · 7 technici",
      result: "Werkelijke kost per serviceoproep, live",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
    ],
  },

  {
    slug: "electricians",
    name: "Elektriciens",
    headline:
      "Tijdsregistratie voor elektriciens — projecturen, budgetbeheer en navigatie in één app",
    intro:
      "Projecturen, een live aftelling van het resterende budget en navigatie naar de werf met één knop — gemaakt voor kleine elektriciensteams die projectbeheer nodig hebben zonder de administratieve last.",
    pains: [
      {
        title: "Uren zijn niet aan het juiste project gekoppeld",
        body: "Wanneer elektriciens in een week aan meerdere jobs werken, leidt het handmatig toewijzen van uren op het einde van de dag tot verkeerd toegewezen kosten, factuurdiscussies en een loonverwerking die je niet kan uitleggen.",
      },
      {
        title: "Budgetoverschrijdingen worden te laat opgemerkt",
        body: "Zonder een live zicht op verbruikte uren versus begrote uren kom je er pas achter dat een project is uitgelopen wanneer je de factuur opmaakt — niet wanneer je nog kon reageren.",
      },
      {
        title: "Tijd verloren met het vinden van de werf",
        body: "Adressen in een aparte app getypt, telefoontjes naar kantoor voor de weg, te laat aankomen op klantlocaties. Elke verloren minuut is een factureerbare minuut verloren.",
      },
      {
        title: "Uren van onderaannemers moeilijk af te stemmen",
        body: "Bij grotere installaties houden onderaannemende elektriciens hun eigen uren apart bij. Een gecombineerd zicht op de arbeidskosten op het einde van een project krijgen is een handmatige, foutgevoelige klus.",
      },
    ],
    features: [
      {
        title: "Projecturen met live resterend budget",
        body: "Elke gewerkte minuut wordt automatisch aan het juiste project gekoppeld. Een live aftelling toont hoeveel uren er nog resten ten opzichte van het budget — voor jou en voor de werknemer op de werf.",
        plan: "Project Basic",
      },
      {
        title: "Navigatie naar de werf met één knop",
        body: "Het projectadres staat in de app. Eén tik opent de navigatie — geen gezoek, geen aparte kaartapp, geen telefoontjes naar kantoor.",
      },
      {
        title: "Check-in van werknemers per project",
        body: "Elke elektricien checkt in bij de juiste job. Je weet altijd wie op dit moment op welke werf zit.",
        plan: "Project Basic",
      },
      {
        title: "Meerwerk geregistreerd op de werf",
        body: "Wanneer de opdracht wijzigt — een onverwachte stroomkring, extra materiaal — wordt het in de app geregistreerd met foto's, voordat de ploeg vertrekt.",
        plan: "Project Basic",
      },
      {
        title: "Overurenregistratie met flexibele vergoeding",
        body: "Overuren worden geregistreerd op het moment dat ze gebeuren. Werknemers kiezen om ze uitbetaald te krijgen of om te zetten in inhaalrust.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Wet van 2027 + CheckIn@Work op grote werven",
      title: "Elektriciens vallen onder de verplichting van 2027 — en onder CheckIn@Work waar de werfdrempel van toepassing is",
      body: "Vanaf 1 januari 2027 moeten alle werkgevers de uren digitaal registreren. Voor elektriciteitswerk op bouwwerven van ≥€500.000 excl. btw is CheckIn@Work nu al verplicht. Done-it dekt beide: het levert de digitale tijdsregistratie die 2027 vereist en synchroniseert met CheckIn@Work wanneer de werfdrempel bereikt is.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Elektricien navigeert naar de job",
        body: "Adres en opdracht staan al in de app. Eén tik opent de navigatie — geen telefoontjes, geen verwarring.",
      },
      {
        step: "Uren koppelen automatisch aan het juiste project",
        body: "De check-in op de werf koppelt elke gewerkte minuut aan het juiste project. De teller voor resterende uren wordt live bijgewerkt.",
      },
      {
        step: "Wijzigingen in de opdracht blijven geregistreerd",
        body: "Meerwerk en materiaal worden op de werf geregistreerd, voordat ze vertrekken. De factuur weerspiegelt wat er werkelijk gebeurd is.",
      },
    ],
    stats: [
      { value: "Live", label: "zicht op resterende uren per project" },
      { value: "0", label: "telefoontjes naar kantoor voor werfadressen" },
      { value: "1 jan 2027", label: "verplichte digitale tijdsregistratie" },
    ],
    quote: {
      text: "We weten in real time of een project zijn budget overschrijdt. Dat alleen al heeft ons dit jaar twee slechte verrassingen bespaard.",
      who: "Zaakvoerder, elektriciteitsbedrijf · 5 elektriciens",
      result: "Budgetoverschrijdingen opgemerkt voordat ze gebeuren",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    ],
  },

  {
    slug: "plumbing",
    name: "Loodgieters",
    headline:
      "Tijdsregistratie voor loodgietersteams — snelle check-in, nauwkeurige loonverwerking en aanwezigheidsbewijs voor elke interventie",
    intro:
      "Dringende interventies, meerdere jobs per dag, uren uit het geheugen ingevuld op het einde van de week — of helemaal niet. Done-it maakt er een klus van één tik op elke werf en een loonverwerking in één klik op het einde van de maand van.",
    pains: [
      {
        title: "Interventie-uren uit het geheugen gereconstrueerd",
        body: "Met meerdere jobs op een dag hebben loodgieters zelden de tijd om nauwkeurige urenstaten in te vullen. Uren worden afgerond of vergeten, en de loonverwerking is in het beste geval een schatting.",
      },
      {
        title: "Geen bewijs van tijd op de werf",
        body: "Wanneer een klant betwist hoe lang een herstelling duurde, is er geen objectief overzicht. Een door GPS bevestigde digitale check-in is makkelijk te tonen — een papieren urenstaat niet.",
      },
      {
        title: "Kilometers tussen jobs niet bijgehouden",
        body: "Verplaatsingen tussen interventies tellen op over de week en de maand, maar zonder automatische registratie worden ze zelden correct gefactureerd of vergoed.",
      },
      {
        title: "Loonvoorbereiding is traag",
        body: "Op het einde van de maand kost het achternalopen van urenstaten, het corrigeren van fouten en het opbouwen van de export voor het sociaal secretariaat tijd die je administratieteam niet zou mogen besteden.",
      },
    ],
    features: [
      {
        title: "Locatiegeverifieerde check-in met één tik bij elke interventie",
        body: "Aankomst en vertrek geregistreerd met één tik, met locatieverificatie van de locatie. Snel genoeg voor dringende oproepen; nauwkeurig genoeg voor discussies.",
        plan: "Time Basic",
      },
      {
        title: "Automatische pauze-aftrek",
        body: "Verplichte pauzes worden automatisch van de totale tijd afgetrokken — niets in te vullen, geen loonfouten.",
        plan: "Time Basic",
      },
      {
        title: "Kilometers vastgelegd per rit",
        body: "Configureer transporttypes en vergoedingstarieven. Kilometers worden automatisch geregistreerd en gekoppeld aan de juiste klant of het juiste project.",
        plan: "Add-on",
      },
      {
        title: "Afwezigheids- en ziektebriefjebeheer",
        body: "Werknemers dienen ziektebriefjes digitaal in vanuit de app. Ondersteunt weerverlet en andere afwezigheidstypes die relevant zijn voor loodgieterswerk buiten.",
      },
      {
        title: "Loonexport in één klik",
        body: "Alle uren- en kilometergegevens exporteer je netjes voor je sociaal secretariaat — geen handmatige voorbereiding, geen Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Wet van 2027",
      title: "Loodgieterswerk valt onder de algemene verplichting tot tijdsregistratie van 2027",
      body: "Vanaf 1 januari 2027 moet elke Belgische werkgever — ook kleine loodgietersteams — een digitaal, nauwkeurig en fraudebestendig overzicht van de arbeidsuren bijhouden. Papieren urenstaten en gedeelde spreadsheets voldoen niet langer. Done-it dekt de vereiste meteen, exporteert naar je sociaal secretariaat en houdt alle registraties GDPR-proof.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Loodgieter komt aan bij de interventie",
        body: "Eén tik checkt in. GPS registreert de locatie en de tijd. Snel genoeg voor een dringende job.",
      },
      {
        step: "Vertrek aangetikt op weg naar buiten",
        body: "De check-out sluit de tijdsregistratie af. De kilometers naar de volgende job beginnen automatisch te registreren.",
      },
      {
        step: "Maandeinde is één klik",
        body: "Alle interventies, uren en kilometers exporteer je netjes. Loonvoorbereiding gaat van een dag naar enkele minuten.",
      },
    ],
    stats: [
      { value: "1 tik", label: "om in te checken — snel genoeg voor dringende oproepen" },
      { value: "1 klik", label: "loonexport naar je sociaal secretariaat" },
      { value: "1 jan 2027", label: "verplichte digitale tijdsregistratie" },
    ],
    quote: {
      text: "We waren vroeger op het einde van de maand een halve ochtend kwijt aan het reconstrueren van uren. Nu exporteren en versturen we gewoon. Het sociaal secretariaat krijgt het netjes binnen.",
      who: "Zaakvoerder, loodgietersbedrijf · 4 loodgieters",
      result: "Loonvoorbereiding van uren naar minuten",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
    ],
  },

  {
    slug: "landscaping",
    name: "Tuinaanleg",
    headline:
      "Tijdsregistratie voor tuinaanlegbedrijven — seizoensploegen, meerdere werven en kilometers in één overzicht",
    intro:
      "Het hoogseizoen betekent veel werven, wisselende ploegen en heel wat kilometers. Done-it houdt elk uur, elke afwezigheid en elke rit georganiseerd — zodat je je op het werk kan focussen, niet op het papierwerk.",
    pains: [
      {
        title: "Seizoensploegen zijn complex te beheren",
        body: "Tijdelijke werknemers, wisselende uurroosters en variërende teamgroottes maken nauwkeurige urenregistratie bijna onmogelijk met papier. Tegen de tijd dat je op het einde van de maand afstemt, ontbreekt er altijd iets.",
      },
      {
        title: "Uren verspreid over veel werven",
        body: "Een ploeg die drie of vier werven per dag bezoekt, genereert uren die correct verdeeld moeten worden. Handmatige invoer leidt tot verkeerde toewijzing en betwiste loonverwerking.",
      },
      {
        title: "Weerverlet is moeilijk te documenteren",
        body: "Weerverlet is een reëel gegeven in de tuinaanleg. Zonder een digitaal overzicht is het aanvragen van de juiste vergoeding en het beheren van eC3.2A traag en foutgevoelig.",
      },
      {
        title: "Kilometers tussen werven blijven onregistreerd",
        body: "Bedrijfsbusjes, persoonlijke voertuigen en carpools rijden de hele dag tussen werven heen en weer. Zonder automatische registratie belanden die kilometers nooit in het kostenplaatje.",
      },
    ],
    features: [
      {
        title: "Ploegoverzicht over meerdere werven",
        body: "Zie alle ploegen, alle werven in real time. Weet wie waar werkt, op dit moment — niet op het einde van de maand.",
      },
      {
        title: "Shiftplanning voor seizoensteams",
        body: "Plan wie waar werkt, behandel last-minute wijzigingen en stuur de planning naar de telefoons van het team — zonder een telefoontje naar elke persoon.",
        plan: "Time Pro",
      },
      {
        title: "Aangepaste afwezigheidstypes",
        body: "Maak aangepaste afwezigheidstypes voor je sector: seizoensgebonden weerverlet, ouderschapsverlof, ziektedagen. Werknemers laden documentatie digitaal op vanuit de app.",
      },
      {
        title: "Kilometers met carpoolregistratie",
        body: "Configureer transporttypes (bedrijfsbusje, persoonlijke wagen, carpool chauffeur vs. passagier) met hun eigen tarieven. Kilometers worden automatisch per rit geregistreerd.",
        plan: "Add-on",
      },
      {
        title: "Overurenregistratie met omzetting naar verlof",
        body: "Overuren in het hoogseizoen stapelen zich snel op. Elke werknemer kan kiezen om ze uitbetaald te krijgen of de uren als inhaalrust op te sparen — transparant bijgehouden.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Wet van 2027 + weerverlet",
      title: "Tuinaanleg valt onder de verplichting van 2027 — en weerverlet voegt een specifieke digitale plicht toe",
      body: "Vanaf 1 januari 2027 moet elke werkgever de uren digitaal registreren. Tuinaanlegteams die buiten werken, krijgen ook vaak te maken met weerverlet (tijdelijke werkloosheid wegens slecht weer). Sinds 1 januari 2025 is de digitale controlekaart eC3.2A verplicht voor tijdelijke werkloosheid in sectoren waaronder buitenwerk. Done-it ondersteunt aangepaste afwezigheidstypes voor weerverlet en levert de digitale tijdsregistratie die de wet van 2027 vereist.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Ploeg checkt in op de eerste werf",
        body: "GPS registreert de aankomst. Uren worden automatisch verdeeld over de juiste locatie en het juiste project — geen handmatige toewijzing op het einde van de dag.",
      },
      {
        step: "Het weer legt het werk stil",
        body: "De ploegbaas registreert weerverlet vanuit de app. Het digitale overzicht ondersteunt de eC3.2A-procedure zonder extra papierwerk.",
      },
      {
        step: "Maandeinde: alle werven, alle ploegen, één export",
        body: "Uren, afwezigheden en kilometers over elke werf en elk ploeglid exporteer je netjes voor de loonverwerking.",
      },
    ],
    stats: [
      { value: "1 jan 2025", label: "eC3.2A digitale kaart verplicht voor tijdelijke werkloosheid" },
      { value: "1 jan 2027", label: "algemene verplichting tot tijdsregistratie" },
      { value: "Automatisch", label: "kilometerregistratie — geen rittenboek meer" },
    ],
    quote: {
      text: "Het hoogseizoen betekende vroeger loonchaos. Nu checkt onze ploegbaas de app, is iedereen geregistreerd, en exporteer ik de uren op de laatste dag van de maand.",
      who: "Zaakvoerder, tuinaanlegbedrijf · 12 seizoenswerknemers",
      result: "Geen reconstructie van de loonverwerking meer op het einde van de maand",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    ],
  },

  {
    slug: "maintenance",
    name: "Onderhoud",
    headline:
      "Tijdsregistratie voor onderhoudsteams — terugkerende werfbezoeken, kost per contract en aanwezigheidsbewijs in één app",
    intro:
      "Terugkerende onderhoudscontracten staan of vallen met zicht op de kosten. Done-it registreert elk bezoek, volgt de uren ten opzichte van het contractbudget en geeft jou — en je klanten — een aanwezigheidsbewijs voor elke oproep.",
    pains: [
      {
        title: "Geen zicht op de kosten per onderhoudscontract",
        body: "Wanneer de uren voor terugkerende bezoeken niet worden bijgehouden ten opzichte van het specifieke contract, weet je pas bij de jaarafrekening of een klant winstgevend is — en dan is het te laat om te herprijzen.",
      },
      {
        title: "Klanten verwachten een bewijs van wat er gedaan is",
        body: "Facility managers en eigenaars willen steeds vaker aanwezigheidsregistraties: wie kwam, wanneer ze aankwamen, wat ze deden. Een papieren logboek dat weken later wordt voorgelegd, voldoet niet aan de SLA-vereisten.",
      },
      {
        title: "Terugkerende bezoeken moeilijk te plannen en te registreren",
        body: "Wisselende shifts over veel terugkerende werven — met personeel dat voor elkaar invalt — creëren een planning- en urenregistratiepuzzel die handmatige processen niet betrouwbaar kunnen oplossen.",
      },
      {
        title: "Weerverlet treft ook buitenonderhoud",
        body: "Tuinonderhoud, gevelonderhoud en buitenfaciliteitenwerk krijgen allemaal te maken met weergerelateerde stilstand. Weerverlet correct documenteren vereist een digitaal spoor.",
      },
    ],
    features: [
      {
        title: "Contractgebaseerde kostenregistratie",
        body: "Elk onderhoudscontract is een project. Uren worden er automatisch op geregistreerd, wat je een live kostencijfer en aftelling van resterende uren per klant geeft.",
        plan: "Project Basic",
      },
      {
        title: "Door GPS bevestigd aanwezigheidsbewijs",
        body: "Tijdstempel, locatiegeverifieerde check-ins zijn een onbetwistbaar overzicht van wie wanneer en hoe lang op de werf was — klaar om op verzoek met klanten te delen.",
      },
      {
        title: "Updates met foto's vanaf de werf",
        body: "Werknemers melden problemen, voltooide taken of meerwerk rechtstreeks vanuit de app — met tekst en foto's. Klanten krijgen een realtime overzicht; jij hebt de documentatie voor de facturatie.",
        plan: "Project Basic",
      },
      {
        title: "Planning van terugkerende shifts",
        body: "Plan terugkerende bezoeken over alle contracten, wijs werknemers toe en behandel vervangingen — zonder een telefoontje naar elke persoon.",
        plan: "Time Pro",
      },
      {
        title: "Afwezigheidstypes voor buitenonderhoud",
        body: "Configureer weerverlet en andere afwezigheidstypes. Werknemers registreren stilstand vanuit de app; documentatie wordt digitaal opgeladen.",
      },
    ],
    compliance: {
      label: "Wet van 2027 + weerverlet",
      title: "Onderhoudsteams vallen onder de verplichting van 2027 en krijgen te maken met weerverlet-specifieke digitale plichten",
      body: "Vanaf 1 januari 2027 moet elke werkgever een digitaal overzicht van de arbeidsuren bijhouden. Voor buitenonderhoudswerk voegt weerverlet (tijdelijke werkloosheid wegens slecht weer) een specifieke plicht toe: sinds 1 januari 2025 is de controlekaart eC3.2A in digitale vorm verplicht. Done-it dekt beide: het levert de algemene tijdsregistratie die 2027 vereist en ondersteunt aangepaste afwezigheidstypes — inclusief weerverlet — met digitale documentatie.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Werknemer komt aan op de onderhoudswerf",
        body: "Locatiegeverifieerde check-in registreert het aankomsttijdstip en koppelt de uren aan het juiste contract. Aanwezigheidsbewijs wordt automatisch aangemaakt.",
      },
      {
        step: "Taken voltooid, problemen geregistreerd",
        body: "Voltooid werk en eventuele problemen worden vanuit de app gemeld met foto's. Klanten zien precies wat er gedaan is.",
      },
      {
        step: "Kost per contract meteen zichtbaar",
        body: "Uren stapelen zich in real time op ten opzichte van het contractbudget. Je kent de marge voordat het contract verlengd wordt.",
      },
    ],
    stats: [
      { value: "Live", label: "kost per onderhoudscontract" },
      { value: "GPS", label: "aanwezigheidsbewijs klaar voor klanten" },
      { value: "1 jan 2027", label: "verplichte digitale tijdsregistratie" },
    ],
    quote: {
      text: "We kunnen een facility manager in twee minuten exact vertellen wie wanneer op de werf was. Dat niveau van bewijs heeft veranderd hoe klanten ons zien.",
      who: "Operationeel manager, onderhoudsbedrijf · 8 werknemers",
      result: "Klantendiscussies teruggebracht tot nul",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
    ],
  },
  {
    slug: "retail",
    name: "Winkels & retail",
    headline: "Tijdregistratie voor winkels — inklokken per shift, vervanging en een loonklare export, in één app",
    intro: "Openingsshiften, avondshiften en altijd wel iemand die voor iemand anders invalt. Done-it geeft elke winkel een heldere digitale registratie van wie welke shift werkte — ingeklokt op de eigen smartphone, klaar voor de loonverwerking op het einde van de maand.",
    pains: [
      {
        title: "Shiftwissels bereiken de loonverwerking nooit",
        body: "Iemand valt in voor een zieke collega, iemand blijft een uur langer bij het sluiten. Het blijft hangen op een briefje, of helemaal niet — en je loon klopt al niet voor je begint.",
      },
      {
        title: "Meerdere winkels, één loonverwerking",
        body: "Medewerkers wisselen van vestiging, deeltijders werken op twee locaties. De uren per winkel verzamelen en manueel samenvoegen kost elke maand een dag.",
      },
      {
        title: "Pauzes en overuren zijn giswerk",
        body: "Wettelijke pauzes worden uit het hoofd afgetrokken en extra uren informeel geregeld — precies de twee dingen waar een controle als eerste naar vraagt.",
      },
    ],
    features: [
      {
        title: "Inklokken op de winkelvloer",
        body: "Eén tik op de eigen smartphone bij de start van de shift. Geen terminal aan de kassa, geen gedeelde badge, geen klembord aan de deur van het magazijn.",
      },
      {
        title: "Uren per winkel, medewerkers over vestigingen heen",
        body: "Elke winkel is een project. Wie in een andere vestiging invalt, klokt in op die vestiging — je ziet de uren waar ze echt gepresteerd zijn.",
      },
      {
        title: "Pauzes automatisch afgetrokken",
        body: "Stel de pauzeregel één keer in en ze geldt voor elke shift. Overuren worden geregistreerd zodra ze gemaakt worden, en elke medewerker kiest zelf tussen uitbetaling of inhaalrust.",
      },
    ],
    compliance: {
      label: "Tijdregistratie per shift",
      title: "Een sluitend bewijs van elke gewerkte shift",
      body: "Done-it houdt een objectieve digitale registratie bij van elk in- en uitklokken, met pauzes en overuren — precies het bewijs waar een audit of een loondiscussie om vraagt.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Medewerkers klokken in voor hun shift", body: "Eén tik op de smartphone bij de start van de shift. Invallen in een andere vestiging wordt op die vestiging geboekt." },
      { step: "Je ziet wie aanwezig is, per winkel", body: "Een live overzicht over alle locaties — een niet-ingevulde openingsshift zie je voor de deuren opengaan." },
      { step: "Loonverwerking in één export", body: "Uren, pauzes en overuren per medewerker en per winkel, klaar geëxporteerd voor je sociaal secretariaat." },
    ],
    stats: [
      { value: "1 tik", label: "om in te klokken — geen terminal aan de kassa" },
      { value: "Live", label: "overzicht van wie aanwezig is, in elke winkel" },
      { value: "0", label: "shiftwissels verloren tussen vloer en loon" },
    ],
    quote: {
      text: "Twee winkels en veel deeltijders. Vroeger werd de helft van de invalshiften uit het hoofd geregeld. Nu staat elk uur op de juiste winkel en kost de loonverwerking minuten.",
      who: "Zaakvoerder, retail · 11 medewerkers",
      result: "Loonvoorbereiding van een dag naar minder dan een uur",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Kantoren & dienstverlening",
    headline: "Tijdregistratie voor kantoren — uren per klant en project, zonder spreadsheet",
    intro: "Accountants, notarissen, architecten, bureaus en consultants factureren hun tijd — of moeten op zijn minst weten waar ze naartoe ging. Done-it registreert uren op de juiste klant en het juiste project, vanaf het bureau of onderweg.",
    pains: [
      {
        title: "Factureerbare uren op vrijdag gereconstrueerd",
        body: "Tijd die je op het einde van de week opschrijft, is tijd die je gokt. Hele uren verdwijnen stilletjes tussen het werk en de factuur.",
      },
      {
        title: "Geen zicht op wat een klant echt kost",
        body: "Het ereloon werd maanden geleden afgesproken. Of het werk er nog binnen past, blijkt pas als iemand het optelt — meestal te laat.",
      },
      {
        title: "Hybride werken maakt de registratie onvolledig",
        body: "Een deel van het team zit op kantoor, een deel thuis, een deel bij de klant. Aanwezigheid is geen feit meer maar een reconstructie.",
      },
    ],
    features: [
      {
        title: "Uren op klant en project",
        body: "Elke registratie komt op een project terecht, zodat je de uren per klant ziet zonder iets te exporteren of iemand iets te vragen.",
      },
      {
        title: "Werkt op kantoor en onderweg",
        body: "Dezelfde app op de smartphone of in de browser. Een klantbezoek telt als werk voor die klant, inclusief de verplaatsing als je dat zo wil.",
      },
      {
        title: "Overuren en verlof op één plek",
        body: "Extra uren geregistreerd op het moment zelf, verlof aangevraagd en goedgekeurd in de app — geen aparte spreadsheet meer van wie wanneer weg is.",
      },
    ],
    compliance: {
      label: "Tijdregistratie per klant",
      title: "Een objectieve registratie van gewerkte uren",
      body: "Done-it houdt een digitale, fraudebestendige registratie bij van de arbeidstijd per medewerker — de basis voor zowel de loonverwerking als een verdedigbare klantfactuur.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Open een klantproject", body: "Eén keer instellen met de klant en, als je op budget factureert, de uren die je gepland hebt." },
      { step: "Het team registreert er tijd op", body: "Vanaf het bureau of de smartphone. Elk uur komt op een klant terecht, niet in iemands geheugen." },
      { step: "Zie uren per klant, exporteer voor het loon", body: "Live totalen per project, en een propere export voor loon of facturatie op het einde van de maand." },
    ],
    stats: [
      { value: "Per klant", label: "uren, zichtbaar terwijl het werk nog loopt" },
      { value: "Kantoor of onderweg", label: "dezelfde registratie, smartphone en browser" },
      { value: "1 export", label: "voor loonverwerking en facturatie" },
    ],
    quote: {
      text: "We bouwden elke vrijdag de hele week opnieuw op. Nu staan de uren al op de juiste klant en zie ik welke dossiers het ereloon opeten.",
      who: "Vennoot, accountantskantoor · 8 medewerkers",
      result: "Elke maand factureerbare uren gerecupereerd",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salons & lichaamsverzorging",
    headline: "Tijdregistratie voor salons — shiften, deeltijdse uren en loon zonder papieren agenda",
    intro: "Kappers, barbiers, nagel- en schoonheidssalons draaien op shiften en deeltijdse contracten. Done-it registreert wie wanneer werkte, trekt pauzes automatisch af en levert de loonverwerking een proper bestand.",
    pains: [
      {
        title: "Deeltijdse contracten, eindeloze varianten",
        body: "Drie dagen de ene week, vier de volgende, een zaterdag geruild voor een dinsdag. Contracturen en gewerkte uren lopen snel uit elkaar.",
      },
      {
        title: "De agenda is geen tijdregistratie",
        body: "Afspraken tonen wat geboekt werd, niet wie werkte. Als het loon of een controle ernaar vraagt, is het boekingssysteem het verkeerde antwoord.",
      },
      {
        title: "Extra uren informeel geregeld",
        body: "Langer blijven voor een laatste klant wordt onthouden, beloofd en soms vergeten. Net zo'n gat groeit uit tot een discussie.",
      },
    ],
    features: [
      {
        title: "Inklokken in het salon",
        body: "Eén tik op de eigen smartphone bij de start van de shift — geen gedeelde terminal aan de balie, geen handtekeningenlijst bij de spiegel.",
      },
      {
        title: "Pauzes en deeltijdregels één keer instellen",
        body: "Stel de pauzeregel en de standaarduren één keer in; ze gelden voor elke shift, met uitzonderingen per persoon waar een contract afwijkt.",
      },
      {
        title: "Overuren: uitbetaald of opgenomen",
        body: "Extra uren worden geregistreerd terwijl ze gepresteerd worden, en elke medewerker kiest tussen uitbetaling of inhaalrust — geregeld in de app, niet uit het hoofd.",
      },
    ],
    compliance: {
      label: "Digitale tijdregistratie",
      title: "Een registratie die verder gaat dan het afsprakenboek",
      body: "Done-it registreert de werkelijke arbeidstijd per medewerker, met pauzes en overuren — los van de boekingen, en verdedigbaar wanneer ze gecontroleerd wordt.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Medewerkers klokken in voor de shift", body: "Eén tik bij aankomst. Pauzes worden afgetrokken volgens de regel die je één keer instelt." },
      { step: "Je volgt de dag terwijl hij loopt", body: "Wie aanwezig is, wie pauze neemt, wie langer bleef — live, zonder te vragen." },
      { step: "De loonverwerking krijgt een proper bestand", body: "Gewerkte uren, pauzes en overuren per medewerker, geëxporteerd voor je sociaal secretariaat." },
    ],
    stats: [
      { value: "1 tik", label: "om in te klokken — niets gedeeld, niets op papier" },
      { value: "Auto", label: "pauzeaftrek op elke shift" },
      { value: "0", label: "late uren uit het hoofd geregeld" },
    ],
    quote: {
      text: "Iedereen werkt deeltijds en elke week is anders. De app registreert het gewoon — ik bouw de maand niet meer opnieuw op in een spreadsheet.",
      who: "Zaakvoerster, kapsalon · 6 medewerkers",
      result: "Geen betwiste uren meer op het einde van de maand",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Gezondheid & wellness",
    headline: "Tijdregistratie voor fitness, studio's en spa's — shiften, lessen en loon in één app",
    intro: "Fitnesscentra, studio's, kinepraktijken en spa's draaien lange openingsuren met wisselende medewerkers. Done-it registreert wie welke shift invulde, trekt pauzes automatisch af en exporteert het loon proper.",
    pains: [
      {
        title: "Lange openingsuren, wisselende bezetting",
        body: "Vroege shiften, avondlessen en weekends, ingevuld door een mix van voltijders, deeltijders en freelancers. Het uurrooster is een plan, geen registratie.",
      },
      {
        title: "Lesuren en vloeruren lopen door elkaar",
        body: "Een lesgever geeft twee lessen en staat er tussenin aan het onthaal. Dat correct betalen betekent weten wat er echt gewerkt is, niet wat gepland stond.",
      },
      {
        title: "Meerdere locaties, één administratie",
        body: "Medewerkers wisselen tussen studio's. Hun uren elke maand manueel samenvoegen is traag en foutgevoelig.",
      },
    ],
    features: [
      {
        title: "Inklokken per studio",
        body: "Eén tik op de smartphone op de locatie waar ze werken. Invallen in een andere studio komt op de uren van die studio.",
      },
      {
        title: "Live zicht op wie aanwezig is",
        body: "Zie de vloer op elke locatie in real time — een niet-ingevulde vroege shift zie je voor de deuren opengaan, niet erna.",
      },
      {
        title: "Pauzes en overuren geregeld",
        body: "Pauzeregels automatisch toegepast, extra uren geregistreerd op het moment zelf, met uitbetaling of inhaalrust per medewerker.",
      },
    ],
    compliance: {
      label: "Shiftregistratie over locaties",
      title: "Een objectieve registratie voor elke shift en locatie",
      body: "Done-it houdt een digitale registratie bij van de arbeidstijd per medewerker en per locatie — de basis voor een correcte betaling en voor elke controle op de gepresteerde uren.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Medewerkers klokken in op locatie", body: "Eén tik bij de start van de shift, op de locatie waar ze werken." },
      { step: "De bezetting is live zichtbaar", body: "Wie op welke locatie aanwezig is, zodat gaten ingevuld raken voor leden ze merken." },
      { step: "Eén export voor de loonverwerking", body: "Uren, pauzes en overuren per medewerker en per studio, klaar voor het sociaal secretariaat." },
    ],
    stats: [
      { value: "Per studio", label: "uren, ook als medewerkers elders invallen" },
      { value: "Live", label: "zicht op de vloer over alle locaties" },
      { value: "Auto", label: "pauzes en overuren, geen rekenwerk" },
    ],
    quote: {
      text: "Twee studio's, lange openingsuren en veel invalshiften. Nu komen de uren op de juiste locatie en is de loonverwerking één bestand.",
      who: "Manager, fitnessstudio · 14 medewerkers",
      result: "Uurrooster en loon kloppen eindelijk met elkaar",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Horeca",
    headline: "Tijdregistratie voor cafés, restaurants en hotels — shiften, pauzes en overuren, eerlijk geregeld",
    intro: "Onderbroken diensten, late sluitingen en een drukke zaterdag die uitloopt. Done-it registreert de uren zoals ze echt gepresteerd worden, trekt pauzes af volgens jouw regel en geeft de loonverwerking één proper bestand.",
    pains: [
      {
        title: "De shift eindigt nooit wanneer het rooster het zegt",
        body: "De service loopt uit, sluiten kost nog een half uur. Die minuten zijn echt werk, en ze uit het hoofd regelen is hoe discussies beginnen.",
      },
      {
        title: "Onderbroken diensten zijn moeilijk te registreren",
        body: "Lunchservice, een pauze, dan het diner. Op papier wordt dat één lang blok of twee gissingen — geen van beide klopt.",
      },
      {
        title: "Veel verloop, constant inwerken",
        body: "Elk seizoen nieuwe medewerkers. Een systeem dat opleiding vraagt, overleeft de eerste drukke week niet.",
      },
    ],
    features: [
      {
        title: "In- en uitklokken per service",
        body: "Eén tik bij de start en het einde van elk blok, zodat onderbroken diensten geregistreerd worden zoals ze gelopen zijn — niet samengevoegd tot één.",
      },
      {
        title: "Pauzes volgens jouw regel",
        body: "Verplichte pauzes automatisch afgetrokken, met uitzonderingen per persoon waar een contract dat vraagt. Geen rekenwerk op het einde van de maand.",
      },
      {
        title: "Overuren geregeld in de app",
        body: "Het extra half uur na sluitingstijd wordt geregistreerd op het moment zelf, en uitbetaald of opgenomen als inhaalrust — de medewerker kiest.",
      },
    ],
    compliance: {
      label: "Registratie van onderbroken diensten",
      title: "Een sluitende registratie van uren in een team met veel verloop",
      body: "Done-it houdt een objectieve digitale registratie bij van elke shift, pauze en extra uur — precies wat een looncontrole of een sociale inspectie wil zien.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Medewerkers klokken in voor de service", body: "Eén tik bij de start van het blok; hetzelfde op het einde. Onderbroken diensten blijven onderbroken." },
      { step: "Je ziet de vloer live", body: "Wie er is voor de service vanavond, wie nog pauze heeft, wie na sluitingstijd bleef." },
      { step: "De loonverwerking krijgt de echte uren", body: "Gewerkte tijd, pauzes en overuren per medewerker, in één bestand geëxporteerd." },
    ],
    stats: [
      { value: "2 blokken", label: "per onderbroken dienst — geen gissing" },
      { value: "Auto", label: "pauzeaftrek volgens jouw regel" },
      { value: "1 min", label: "om een nieuwe medewerker op te starten — op zijn eigen smartphone" },
    ],
    quote: {
      text: "Sluiten loopt altijd uit. Nu worden die minuten geregistreerd in plaats van betwist, en medewerkers kunnen ze opnemen als inhaalrust.",
      who: "Zaakvoerder, restaurant · 17 medewerkers",
      result: "Minder discussies, snellere loonverwerking",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Zorg & gezondheid",
    headline: "Tijdregistratie voor zorgteams — shiften, huisbezoeken en kwalificaties, in één registratie",
    intro: "Praktijken, thuiszorgteams en woonzorgcentra draaien op shiften, bezoeken en strikte registratie. Done-it registreert de gewerkte tijd per shift en per bezoek, en houdt de documenten van je medewerkers op een vindbare plek.",
    pains: [
      {
        title: "Bezoeken en shiften in aparte systemen",
        body: "Huisbezoeken zitten in de ene tool, geplande uren in de andere, en de loonverwerking krijgt een derde versie. Dat op elkaar afstemmen is elke maand weer werk.",
      },
      {
        title: "Nacht- en weekenduren verkeerd verloond",
        body: "Onregelmatige uren hebben andere tarieven. Als de registratie bij benadering is, is het loon dat ook — en de correctie komt achteraf.",
      },
      {
        title: "Attesten vervallen ongemerkt",
        body: "Kwalificaties en attesten tellen in de zorg, en ze liggen in een map die niemand opent tot een inspectie ernaar vraagt.",
      },
    ],
    features: [
      {
        title: "Inklokken per shift of per bezoek",
        body: "Eén tik bij de start van de shift, of op elk klantadres bij thuiszorg — met locatiecontrole waar je het bewijs nodig hebt.",
      },
      {
        title: "Uren opgesplitst zoals je ze uitbetaalt",
        body: "Nacht-, weekend- en gewone uren worden apart geregistreerd, zodat de loonexport al weergeeft hoe het werk echt betaald wordt.",
      },
      {
        title: "Personeelsdocumenten op één plek",
        body: "Contracten, attesten en identiteitsbewijzen bewaard bij de persoon waar ze bij horen — in seconden gevonden als iemand ernaar vraagt.",
      },
    ],
    compliance: {
      label: "Shift- & bezoekregistratie",
      title: "Registraties die een inspectie doorstaan",
      body: "Done-it houdt een digitale, objectieve registratie bij van de arbeidstijd per medewerker — uren, pauzes en de opsplitsing van onregelmatige uren — naast de documenten die bij elke persoon horen.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Inklokken voor de shift of het bezoek", body: "Eén tik op de locatie of op het adres van de klant, met locatiecontrole waar bewijs nodig is." },
      { step: "Uren komen in de juiste categorie", body: "Gewone, nacht- en weekenduren apart geregistreerd op het moment zelf." },
      { step: "Exporteren voor de loonverwerking", body: "Eén proper bestand per periode, al opgesplitst zoals je uitbetaalt." },
    ],
    stats: [
      { value: "Per bezoek", label: "of per shift — wat jouw team ook draait" },
      { value: "Apart", label: "nacht- en weekenduren, geregistreerd zoals gewerkt" },
      { value: "Bewaard", label: "attesten opgeslagen per medewerker" },
    ],
    quote: {
      text: "Huisbezoeken en geplande shiften zitten eindelijk in dezelfde registratie. De loonverwerking is geen puzzelwerk meer.",
      who: "Coördinator, thuiszorgteam · 22 medewerkers",
      result: "Eén registratie in plaats van drie",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
